import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { resolveSceneById } from '../tools/scene-addressing';
import { Tool } from '../tools/Tool';
import type { EnhancedToolResult, ToolDefinition } from '../types';

import { MOTION_GRAPHICS_SCENE_DESIGN_ID } from 'js/src/agent/tools/motion-graphics/motionGraphicsSceneDesign';
import type { RootState } from 'js/src/libs/RootState';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';

/** The scene comes along because it, not the composition, owns the graphic's playback duration. */
export type ResolveAssetResult = { asset: HtmlAsset; scene: Scene } | { error: EnhancedToolResult };

export const designTargetSchemaFields = {
    scene_id: z
        .string()
        .describe('The motion graphics scene whose graphic this call acts on.')
        .optional(),
};

export function resolveDesignTarget(
    state: RootState,
    luminary: Luminary,
    sceneId: string | undefined,
): ResolveAssetResult {
    if (!sceneId) {
        return {
            error: {
                result:
                    'Error: scene_id is required — name the motion graphics scene this call ' +
                    'acts on, exactly as it appears in the current video state.',
                isSuccess: false,
            },
        };
    }
    const resolved = resolveSceneById(luminary, state, sceneId);
    if ('error' in resolved) {
        return { error: { result: `Error: ${resolved.error}`, isSuccess: false } };
    }
    const { scene } = resolved;
    const asset = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE)[0] as HtmlAsset | undefined;
    if (!asset) {
        return {
            error: {
                result:
                    `Error: scene ${sceneId} is not a motion graphics scene — it has no ` +
                    `graphic to design. Add one with ${TOOL_NAMES.addScene} using scene_design_id ` +
                    `"${MOTION_GRAPHICS_SCENE_DESIGN_ID}", or use the regular scene tools to ` +
                    'change this scene.',
                isSuccess: false,
            },
        };
    }
    return { asset, scene };
}

export abstract class DesignTool<TSchema extends z.ZodType = z.ZodType> extends Tool<TSchema> {
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        const required = parameters.required ?? [];
        return {
            ...parameters,
            required: required.includes('scene_id') ? required : [...required, 'scene_id'],
        };
    }

    protected resolveTarget(sceneId: string | undefined): ResolveAssetResult {
        return resolveDesignTarget(this.getState(), this.luminary, sceneId);
    }
}
